/**
 * Unicode to CP437 mapping
 *
 * Maps Unicode code points to their CP437 equivalents for rendering
 * text with DOS bitmap fonts. ASCII (0-127) maps directly; this table
 * covers extended characters like box drawing, blocks, and symbols.
 */
export const unicodeToCp437: Record<number, number> = {
  // Box drawing (single)
  0x250c: 0xda, // ┌
  0x2500: 0xc4, // ─
  0x2510: 0xbf, // ┐
  0x2502: 0xb3, // │
  0x2514: 0xc0, // └
  0x2518: 0xd9, // ┘
  0x251c: 0xc3, // ├
  0x2524: 0xb4, // ┤
  0x252c: 0xc2, // ┬
  0x2534: 0xc1, // ┴
  0x253c: 0xc5, // ┼
  // Box drawing (double)
  0x2551: 0xba, // ║
  0x2550: 0xcd, // ═
  0x2554: 0xc9, // ╔
  0x2557: 0xbb, // ╗
  0x255a: 0xc8, // ╚
  0x255d: 0xbc, // ╝
  0x2560: 0xcc, // ╠
  0x2563: 0xb9, // ╣
  0x2566: 0xcb, // ╦
  0x2569: 0xca, // ╩
  0x256c: 0xce, // ╬
  // Blocks
  0x2591: 0xb0, // ░
  0x2592: 0xb1, // ▒
  0x2593: 0xb2, // ▓
  0x2588: 0xdb, // █
  0x2580: 0xdf, // ▀
  0x2584: 0xdc, // ▄
  0x258c: 0xdd, // ▌
  0x2590: 0xde, // ▐
  // Arrows
  0x2190: 0x1b, // ←
  0x2191: 0x18, // ↑
  0x2192: 0x1a, // →
  0x2193: 0x19, // ↓
  0x2194: 0x1d, // ↔
  0x2195: 0x12, // ↕
  // Math
  0x00b1: 0xf1, // ±
  0x00d7: 0x00, // × (not in CP437, use placeholder)
  0x00f7: 0xf6, // ÷
  0x2264: 0xf3, // ≤
  0x2265: 0xf2, // ≥
  0x2260: 0x00, // ≠ (not in CP437)
  0x2248: 0xf7, // ≈
  0x221e: 0xec, // ∞
  0x221a: 0xfb, // √
  0x222b: 0x00, // ∫ (not in CP437)
  // Greek lowercase
  0x03b1: 0xe0, // α
  0x03b2: 0xe1, // β
  0x03b3: 0xe2, // γ
  0x03b4: 0xeb, // δ
  0x03b5: 0xee, // ε
  0x03b6: 0x00, // ζ
  0x03b7: 0x00, // η
  0x03b8: 0xe9, // θ
  0x03b9: 0x00, // ι
  0x03ba: 0x00, // κ
  0x03bb: 0x00, // λ
  0x03bc: 0xe6, // μ
  0x03bd: 0x00, // ν
  0x03be: 0x00, // ξ
  0x03c0: 0xe3, // π
  0x03c1: 0x00, // ρ
  0x03c3: 0xe5, // σ
  0x03c4: 0xe7, // τ
  0x03c5: 0x00, // υ
  0x03c6: 0xed, // φ
  0x03c7: 0x00, // χ
  0x03c8: 0x00, // ψ
  0x03c9: 0xea, // ω
  // Misc symbols
  0x263a: 0x01, // ☺
  0x263b: 0x02, // ☻
  0x2665: 0x03, // ♥
  0x2666: 0x04, // ♦
  0x2663: 0x05, // ♣
  0x2660: 0x06, // ♠
  0x2022: 0x07, // •
  0x25d8: 0x08, // ◘
  0x25cb: 0x09, // ○
  0x25d9: 0x0a, // ◙
  0x2642: 0x0b, // ♂
  0x2640: 0x0c, // ♀
  0x266a: 0x0d, // ♪
  0x266b: 0x0e, // ♫
  0x263c: 0x0f, // ☼
  0x25ba: 0x10, // ►
  0x25c4: 0x11, // ◄
  0x25b2: 0x1e, // ▲
  0x25bc: 0x1f, // ▼
};

/**
 * Convert a Unicode character code to CP437
 * ASCII (0-127) passes through; extended chars use the mapping table
 */
export function toCP437(unicodeCode: number): number {
  return unicodeToCp437[unicodeCode] ?? (unicodeCode < 128 ? unicodeCode : 32);
}
